/**
 * CalendarSyncPanels — the three Google Calendar connect surfaces (CC asks 1, 2
 * and 4, built 2026-09-18).
 *
 * Google Calendar here is a TENANT-level Google Workspace service account with
 * domain-wide delegation. There is deliberately no "Sign in with Google" and no
 * per-person consent: an admin ingests one service-account key, and after that
 * each staff member gets a connection record. These panels are the whole of that
 * flow in Studio; before them there was no way to connect a calendar at all.
 *
 * Everything is driven by the typed `@v0y4ge/api-client` calendar-sync ops
 * against observed requests — no seed data, no optimistic state, every mutation
 * awaited and followed by a re-read.
 *
 * The error vocabulary is the backend's, branched on `error.code` and never on
 * the HTTP status (an unrouted path also answers 404, and that must not read as
 * "this person has no calendar"). The ingest route is the exception: it emits
 * one unregistered `VALIDATION_ERROR` for three different problems, so for that
 * route alone the server's MESSAGE is shown rather than a mapped sentence.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Btn, Card, ConfirmDialog, Icon, Input, Pill, T, tx } from "@v0y4ge/jrni-ui";
import {
  getCalendarSyncProviders, startCalendarSync, getCalendarSyncConnection,
  deleteCalendarSyncConnection, getCalendarSyncStatus, getCalendarSyncHealth,
  ingestGsuiteCredentials, getGsuiteCredentialsStatus, deleteGsuiteCredentials,
  isApiError,
} from "@v0y4ge/api-client";
import { useT } from "../../lib/i18n";
import { useNav } from "../../shell/router";
import { ProviderLogo } from "./ProviderLogo";
import { friendlyDateTime } from "../../lib/datetime";

const GOOGLE = "google_calendar";
/** Microsoft 365 Calendar's provider id (CC 0112: the relabelled exchange_calendar). */
const MICROSOFT = "exchange_calendar";
const PROVIDER_LABEL: Record<string, string> = { google_calendar: "Google Calendar", exchange_calendar: "Microsoft 365 Calendar" };

/** The tenant-side setup of one provider, as every calendar-sync read reports it. */
type TenantConfig = { configured: boolean; issues: string[]; workspace_domain: string | null };

function errText(e: unknown, fallback: string): string {
  if (isApiError(e)) {
    /* `message` is the right thing to show for the gsuite ingest route, whose
       three distinct 400s share one code; for the calendar-sync routes the code
       is specific and the message is already the sentence we want. */
    const m = (e as { message?: string }).message;
    if (typeof m === "string" && m.trim()) return m;
  }
  return fallback;
}

/* ═══ ASK 1 — the service-account key ══════════════════════════════════════ */

/**
 * The one-time tenant setup. Three fields; the key is a secret, so it is never
 * echoed back and never prefilled — on success the field is cleared and the
 * panel reports only what the server returns (the account, the domain, and that
 * the key is sealed). The key itself is not in the response and cannot be shown.
 */
export function GoogleServiceAccountForm({ canWrite, onConfigured }: { canWrite: boolean; onConfigured?: () => void }) {
  const t = useT();
  const [cfg, setCfg] = useState<TenantConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [email, setEmail] = useState("");
  const [key, setKey] = useState("");
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState<{ email: string; domain: string } | null>(null);
  /** What the tenant actually has stored — which account, which domain. */
  const [stored, setStored] = useState<{ configured: boolean; email: string; domain: string } | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeAsk, setRemoveAsk] = useState(false);
  const [removeErr, setRemoveErr] = useState("");
  const [blockedBy, setBlockedBy] = useState<number | null>(null);
  /* A stored registration is EDITED, not re-entered: the form starts closed and
     the identity fields prefill, so replacing a key is one field rather than
     three (CC / JAI-1826, 2026-09-23). An unconfigured tenant sees the form
     open — there is nothing to look at and the next act is to fill it in. */
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      /* Two reads, answering different questions: the provider read says whether
         calendar sync considers the tenant configured (and names what is
         missing); the credentials read says WHAT is stored. The second was
         impossible until JAI-1826 — the registration could be reported exactly
         once, in the answer to the request that stored it. */
      const [res, cred] = await Promise.all([
        getCalendarSyncProviders(),
        getGsuiteCredentialsStatus().catch(() => null),
      ]);
      const g = res.data.find((p: { id: string }) => p.id === GOOGLE);
      setCfg(g ? (g.tenant_configuration as TenantConfig) : null);
      const d = cred ? (cred as { data?: { configured?: boolean; service_account_email?: string | null; workspace_domain?: string | null } }).data : null;
      const next = d
        ? { configured: !!d.configured, email: String(d.service_account_email || ""), domain: String(d.workspace_domain || "") }
        : null;
      setStored(next);
      /* Prefill so "Replace the key" is one field. Never the key itself — it is
         not returned, and could not be. */
      if (next && next.configured) {
        setEmail((v) => v || next.email);
        setDomain((v) => v || next.domain);
      }
      setEditing(!(next && next.configured));
    } catch (e) {
      setCfg(null);
      setStored(null);
      setLoadError(errText(e, t("settings.calendarSync.configurationReadFailed", "Couldn't read Google Calendar configuration.")));
    } finally { setLoading(false); }
  }, [t]);
  useEffect(() => { void load(); }, [load]);

  const submit = async () => {
    if (busy || !canWrite) return;
    setBusy(true); setErr(""); setOk(null);
    try {
      const res = await ingestGsuiteCredentials({ service_account_email: email.trim(), private_key: key, workspace_domain: domain.trim() });
      const d = (res as { data?: { service_account_email?: string; workspace_domain?: string } }).data || {};
      setOk({ email: String(d.service_account_email || ""), domain: String(d.workspace_domain || "") });
      setKey(""); // the secret leaves the page the moment it is accepted
      setEditing(false);
      await load();
      onConfigured?.();
    } catch (e) {
      /* CONFIGURATION_ERROR is an operator problem, not the admin's mistake:
         the deployment has no key-encryption secret, so the route refuses to
         store a key unsealed. Saying "check your details" there would send
         someone to re-type a perfectly good key. */
      setErr(isApiError(e) && (e as { code?: string }).code === "CONFIGURATION_ERROR"
        ? t("settings.calendarSync.notOnThisDeployment", "Not available on this deployment yet — it has no key-encryption secret, so a service-account key cannot be stored. This needs an operator, not a different key.")
        : errText(e, t("settings.calendarSync.ingestFailed", "Couldn't store the service account.")));
    } finally { setBusy(false); }
  };

  /**
   * Remove the registration.
   *
   * The 409 is not an error to apologise for — it is the platform declining to
   * leave the tenant somewhere nobody can explain: taking the key away
   * disconnects no one, it leaves every staff connection failing at credential
   * resolution instead. So it renders as an INSTRUCTION carrying the count the
   * response gives, not as "something went wrong".
   */
  const remove = async () => {
    if (removing) return;
    setRemoving(true); setRemoveErr(""); setBlockedBy(null);
    try {
      await deleteGsuiteCredentials();
      /* `removed: false` needs no special case: a replay and a never-configured
         tenant both end with nothing stored, which is what the re-read shows. */
      setRemoveAsk(false);
      setKey(""); setEmail(""); setDomain(""); setOk(null);
      await load();
    } catch (e) {
      const code = isApiError(e) ? (e as { code?: string }).code : "";
      if (code === "CALENDAR_SYNC_REGISTRATION_CONFLICT") {
        const p = isApiError(e) ? ((e as { params?: { connectedCalendars?: number } }).params || {}) : {};
        setBlockedBy(typeof p.connectedCalendars === "number" ? p.connectedCalendars : 0);
      } else {
        setRemoveErr(errText(e, t("settings.calendarSync.removeFailed", "Couldn't remove the service account.")));
      }
    } finally { setRemoving(false); }
  };

  const configured = !!cfg && cfg.configured;
  return (
    <Card style={{ padding: 16, marginTop: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <Icon.lock size={14} />
        <b style={{ fontSize: 13.5, color: T.n1 }}>{t("settings.calendarSync.saTitle", "Google service account")}</b>
        {loading || loadError ? null : <Pill tone={configured ? "success" : "neutral"}>{configured ? t("settings.calendarSync.stored", "Key stored") : t("settings.calendarSync.notSetUp", "Not set up")}</Pill>}
      </div>
      <div style={{ fontSize: 12, color: T.soft, lineHeight: 1.55, marginBottom: 12 }}>
        {t("settings.calendarSync.saSub", "One service account for the whole workspace, with domain-wide delegation. Staff do not sign in individually — once this is stored you can connect each person's calendar.")}
      </div>

      {loadError ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, color: "#8C1D18", background: "#FCEEEE", border: "1px solid #F3C9C6", borderRadius: 8, padding: "8px 10px", marginBottom: 12 }}>
          <span style={{ fontSize: 12, lineHeight: 1.5 }}>{loadError}</span>
          <Btn variant="secondary" size="sm" disabled={loading} onClick={() => { void load(); }}>{t("common.actions.retry", "Retry")}</Btn>
        </div>
      ) : null}

      {!loadError && configured && cfg ? (
        <div style={{ display: "grid", gap: 8, padding: "10px 12px", background: "#F3FAF5", border: "1px solid #CDE9D6", borderRadius: 8, marginBottom: 12 }}>
          {/* WHICH registration, not merely THAT there is one (CC / JAI-1826).
              An administrator asked which service account and which domain are
              in use and could not be told — the registration was reportable
              exactly once, in the answer to the request that stored it. The
              service account comes off the credentials read; the domain prefers
              it too and falls back to the provider read, which resolves the
              same value. The key is never here: it is not returned, and could
              not be. */}
          {stored && stored.email ? (
            <span style={{ fontSize: 12.5, color: T.n1 }}>
              <span style={{ color: T.soft }}>{t("settings.calendarSync.saEmail", "Service account email")}: </span>
              <b style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 11.5 }}>{stored.email}</b>
            </span>
          ) : null}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 18, fontSize: 12.5, color: T.n1 }}>
            <span><span style={{ color: T.soft }}>{t("settings.calendarSync.domain", "Workspace domain")}: </span><b>{(stored && stored.domain) || cfg.workspace_domain || "—"}</b></span>
            <span><span style={{ color: T.soft }}>{t("settings.calendarSync.keyState", "Private key")}: </span><b>{t("settings.calendarSync.sealed", "stored and sealed")}</b></span>
          </div>
          {canWrite ? (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
              {!editing ? (
                <Btn variant="secondary" size="sm" onClick={() => setEditing(true)}>{t("settings.calendarSync.replaceKey", "Replace the key")}</Btn>
              ) : null}
              {!removeAsk ? (
                <Btn variant="ghost" size="sm" onClick={() => { setRemoveAsk(true); setBlockedBy(null); setRemoveErr(""); }}>{t("settings.calendarSync.remove", "Remove")}</Btn>
              ) : null}
            </div>
          ) : null}

          {/* The 409 rendered as the instruction it is, with the count the
              response carried — so the next step is known without a second
              request and without counting staff by hand. */}
          {blockedBy !== null ? (
            <div style={{ fontSize: 11.5, color: "#916D0F", background: "#FAF1DE", border: "1px solid #EBD9AE", borderRadius: 8, padding: "8px 10px", lineHeight: 1.5 }}>
              {blockedBy === 1
                ? t("settings.calendarSync.blockedOne", "Disconnect 1 staff calendar before removing this — removing the key would not disconnect anyone, it would leave their sync failing with nothing to explain it.")
                : t("settings.calendarSync.blockedMany", "Disconnect {n} staff calendars before removing this — removing the key would not disconnect anyone, it would leave their sync failing with nothing to explain it.").replace("{n}", String(blockedBy))}
            </div>
          ) : null}
          {removeErr ? <div style={{ fontSize: 11.5, color: "#8C1D18", lineHeight: 1.5 }}>{removeErr}</div> : null}

          {removeAsk && blockedBy === null ? (
            <div style={{ display: "grid", gap: 8, padding: 10, border: "1px solid #F3C9C6", background: "#FDF6F6", borderRadius: 8 }}>
              <span style={{ fontSize: 12, color: T.n1, lineHeight: 1.5 }}>
                {t("settings.calendarSync.removeAsk", "Remove this service account? Staff calendars cannot be connected again until a new key is stored. The key itself is already sealed and is not recoverable either way.")}
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <Btn variant="danger" size="sm" disabled={removing} onClick={() => { void remove(); }}>{removing ? t("settings.calendarSync.removing", "Removing…") : t("settings.calendarSync.removeConfirm", "Remove it")}</Btn>
                <Btn variant="ghost" size="sm" disabled={removing} onClick={() => setRemoveAsk(false)}>{t("common.actions.cancel", "Cancel")}</Btn>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {!loadError && !configured && cfg && cfg.issues.length ? (
        <ul style={{ margin: "0 0 12px", paddingLeft: 18, fontSize: 12, color: "#916D0F", lineHeight: 1.6 }}>
          {cfg.issues.map((i) => <li key={i}>{i}</li>)}
        </ul>
      ) : null}

      {canWrite && !loadError && (editing || !configured) ? (
        <div style={{ display: "grid", gap: 10, maxWidth: 520 }}>
          {/* With the identity fields prefilled from what is stored, replacing
              a key is ONE field rather than three (CC / JAI-1826). The key is
              never prefilled — nothing returns it. */}
          <label style={{ display: "grid", gap: 4 }}>
            <span style={{ fontSize: 12, color: T.soft }}>{t("settings.calendarSync.saEmail", "Service account email")}</span>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("settings.calendarSyncPanels.placeholder", "studio-sync@project.iam.gserviceaccount.com")} disabled={busy} />
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span style={{ fontSize: 12, color: T.soft }}>{t("settings.calendarSync.privateKey", "Private key")}</span>
            <textarea
              value={key}
              onChange={(e) => setKey(e.target.value)}
              disabled={busy}
              rows={5}
              spellCheck={false}
              placeholder={t("settings.calendarSyncPanels.placeholder2", "-----BEGIN PRIVATE KEY----- … -----END PRIVATE KEY-----")}
              style={{ width: "100%", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 11.5, lineHeight: 1.5, padding: "8px 10px", border: `1px solid ${T.border}`, borderRadius: 8, resize: "vertical", background: busy ? "#F7F7F9" : "#FFFFFF" }}
            />
            <span style={{ fontSize: 11, color: T.soft }}>{t("settings.calendarSync.keyNeverShown", "Paste the whole PEM block. It is stored sealed and never shown again — replacing it means pasting a new one.")}</span>
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span style={{ fontSize: 12, color: T.soft }}>{t("settings.calendarSync.workspaceDomain", "Workspace domain")}</span>
            <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder={t("settings.calendarSyncPanels.placeholder3", "example.edu")} disabled={busy} />
          </label>
          {err ? <div style={{ fontSize: 12, color: "#8C1D18", background: "#FCEEEE", border: "1px solid #F3C9C6", borderRadius: 8, padding: "8px 10px", lineHeight: 1.5 }}>{err}</div> : null}
          {ok ? <div style={{ fontSize: 12, color: "#0B6B3A", background: "#F3FAF5", border: "1px solid #CDE9D6", borderRadius: 8, padding: "8px 10px", lineHeight: 1.5 }}>{t("settings.calendarSync.storedFor", "Stored for")} <b>{ok.email}</b> · {ok.domain}</div> : null}
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="primary" size="sm" disabled={busy || !email.trim() || !key.trim() || !domain.trim()} onClick={() => { void submit(); }}>
              {busy ? t("settings.calendarSync.storing", "Storing…") : configured ? t("settings.calendarSync.replaceKey", "Replace the key") : t("settings.calendarSync.storeKey", "Store the key")}
            </Btn>
            {configured ? (
              <Btn variant="ghost" size="sm" disabled={busy} onClick={() => { setEditing(false); setKey(""); setErr(""); }}>{t("common.actions.cancel", "Cancel")}</Btn>
            ) : null}
          </div>
        </div>
      ) : null}
    </Card>
  );
}

/* ═══ ASK 2 — one staff member's connection ════════════════════════════════ */

/**
 * Connect / disconnect for one person, one row per provider (CC 0127), in the staff
 * editor where the dead "Calendar sync" toggle used to be.
 *
 * The domain check happens BEFORE submit, against
 * `tenant_configuration.workspace_domain` — the very value the server compares
 * against — so the two cannot disagree. Without it the visitor learns their
 * address is wrong from a 422 after pressing Connect.
 */
export function StaffCalendarConnection({ staffId, staffEmail, canWrite }: { staffId: string; staffEmail?: string; canWrite: boolean }) {
  const t = useT();
  const nav = useNav();
  /* ONE ROW PER PROVIDER, for everyone (CC 0127, operator 2026-10-01). A person may
     hold a Google AND a Microsoft 365 connection at once (one per provider), and each
     row carries its own state: connected (status, mailbox, calendar, direction, its
     error, Disconnect for that provider), not connected (Connect opens that
     provider's form), or not set up (disabled, with its reason and the link). */
  type Prov = { id: string; label: string; cfg: TenantConfig | null };
  const [state, setState] = useState<{ phase: "loading" | "ready" | "error"; conns: Record<string, any>; provs: Prov[]; msg?: string }>({ phase: "loading", conns: {}, provs: [] });
  const [openFor, setOpenFor] = useState<string>("");
  const [email, setEmail] = useState(staffEmail || "");
  const [calendarId, setCalendarId] = useState("primary");
  const [direction, setDirection] = useState<"bidirectional" | "push_only" | "pull_only">("bidirectional");
  const [busy, setBusy] = useState(false);
  const [rowErr, setRowErr] = useState<{ provider: string; text: string } | null>(null);
  const [justConnected, setJustConnected] = useState("");
  const [confirmingDisconnect, setConfirmingDisconnect] = useState("");

  /* The editor reuses this component when the route moves to another staff
     member, so every piece of per-person state is reset here. Without it the
     previous person's typed address would survive into the next person's
     Connect form — a connect to the wrong mailbox is one click away. */
  useEffect(() => {
    setEmail(staffEmail || "");
    setOpenFor("");
    setRowErr(null);
    setJustConnected("");
    setConfirmingDisconnect("");
  }, [staffId, staffEmail]);

  /* The staff member whose read may land. A slower read for the PREVIOUS person
     must not overwrite this person's rows: Disconnect targets `staffId`, so a
     stale row would name one mailbox while disconnecting another. */
  const currentStaff = useRef(staffId);
  currentStaff.current = staffId;
  const load = useCallback(async () => {
    const forStaff = staffId;
    setState((s) => ({ ...s, phase: "loading" }));
    try {
      const [providers, gConn, mConn] = await Promise.all([
        getCalendarSyncProviders(),
        getCalendarSyncConnection(forStaff, GOOGLE).catch(() => null),
        getCalendarSyncConnection(forStaff, MICROSOFT).catch(() => null),
      ]);
      if (currentStaff.current !== forStaff) return;
      const list = (providers.data as { id: string; label?: string; tenant_configuration?: unknown }[]);
      const provs: Prov[] = [GOOGLE, MICROSOFT].map((id) => {
        const p = list.find((x) => x.id === id);
        return { id, label: p?.label || PROVIDER_LABEL[id], cfg: p ? (p.tenant_configuration as TenantConfig) : null };
      }).filter((p) => list.some((x) => x.id === p.id));
      const conns: Record<string, any> = {};
      const g = gConn ? (gConn as any).data : null, m = mConn ? (mConn as any).data : null;
      if (g) conns[GOOGLE] = g;
      if (m) conns[MICROSOFT] = m;
      setState({ phase: "ready", conns, provs });
    } catch (e) {
      if (currentStaff.current !== forStaff) return;
      setState({ phase: "error", conns: {}, provs: [], msg: errText(e, t("settings.calendarSync.readFailed", "Couldn't read the calendar connection.")) });
    }
  }, [staffId, t]);
  useEffect(() => { void load(); }, [load]);

  const setupHint = (id: string) => id === MICROSOFT
    ? "The Microsoft app registration isn't ready yet. An admin sets it up once in Settings > Integrations > Microsoft, then this will work."
    : t("settings.calendarSync.setupFirst", "The Google service account has not been stored yet — an admin sets it up once in the Google integration, then this will work.");

  const connect = async (provider: string, domain: string) => {
    if (busy) return;
    setBusy(true); setRowErr(null);
    try {
      await startCalendarSync({ staff_id: staffId, provider, calendar_id: calendarId.trim() || "primary", account_email: email.trim(), sync_direction: direction });
      setJustConnected(provider);
      setOpenFor("");
      await load();
    } catch (e) {
      const code = isApiError(e) ? (e as { code?: string }).code : "";
      const params = isApiError(e) ? ((e as { params?: { workspaceDomain?: string } }).params || {}) : {};
      setRowErr({ provider, text:
        code === "CALENDAR_SYNC_ACCOUNT_DOMAIN_INVALID"
          ? t("settings.calendarSync.domainInvalid", "That address is not in the workspace domain") + (params.workspaceDomain ? ` (${params.workspaceDomain}).` : domain ? ` (${domain}).` : ".")
          : code === "CALENDAR_SYNC_PROVIDER_SETUP_REQUIRED"
            ? setupHint(provider)
            : errText(e, t("settings.calendarSync.connectFailed", "Couldn't connect the calendar.")) });
    } finally { setBusy(false); }
  };

  const disconnect = async (provider: string) => {
    if (busy) return;
    setBusy(true); setRowErr(null);
    try { await deleteCalendarSyncConnection(staffId, provider); if (justConnected === provider) setJustConnected(""); await load(); }
    catch (e) { setRowErr({ provider, text: errText(e, t("settings.calendarSync.disconnectFailed", "Couldn't disconnect the calendar.")) }); }
    finally { setBusy(false); setConfirmingDisconnect(""); }
  };

  /* Layout (operator, 6 Oct): one card per provider, logo + name + one status line, the
     action on the right; details as labelled facts; the connect form inside its card. */
  const DIRECTIONS: { id: "bidirectional" | "push_only" | "pull_only"; label: string; sub: string }[] = [
    { id: "bidirectional", label: t("settings.calendarSync.twoWay", "Two-way"), sub: "Bookings are added to the calendar, and its events block availability." },
    { id: "push_only", label: t("settings.calendarSync.pushOnly", "Push only"), sub: "Bookings are added to the calendar. Its events don't block availability." },
    { id: "pull_only", label: t("settings.calendarSync.pullOnly", "Pull only"), sub: "Its events block availability. Bookings aren't added to it." },
  ];
  const dirLabel = (d: string) => DIRECTIONS.find((x) => x.id === d)?.label ?? (d ? d.replace(/_/g, " ") : "—");
  const calLabel = (c?: string) => (!c || c === "primary" ? "Primary calendar" : c);
  const fieldLabel = { fontSize: 12, fontWeight: 600, color: T.n1 } as const;
  const help = { fontSize: 11.5, color: T.soft, lineHeight: 1.5 } as const;

  if (state.phase === "loading") {
    return (
      <div role="status" aria-label={t("common.loading", "Loading…")} style={{ display: "grid", gap: 12 }}>
        {[0, 1].map((i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: 18, border: `1px solid ${T.border}`, borderRadius: 12, background: T.white }}>
            <span style={{ width: 40, height: 40, borderRadius: 10, background: T.n6 }} />
            <span style={{ flex: 1, display: "grid", gap: 6 }}><span style={{ width: "30%", height: 12, borderRadius: 4, background: T.n6 }} /><span style={{ width: "50%", height: 10, borderRadius: 4, background: T.n6 }} /></span>
          </div>
        ))}
      </div>
    );
  }
  if (state.phase === "error") {
    return (
      <div role="alert" style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 10, background: "#FDECEA", border: "1px solid #F3D0CB", fontSize: 12.5, color: "#8C1D18" }}>
        <Icon.warn size={14} /><span style={{ flex: 1 }}>{state.msg}</span>
        <Btn variant="secondary" size="sm" onClick={() => { void load(); }}>{t("common.actions.retry", "Retry")}</Btn>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {state.provs.map((p) => {
        const conn = state.conns[p.id] ?? null;
        const ready = !!p.cfg?.configured;
        const issues = Array.isArray(p.cfg?.issues) ? (p.cfg!.issues as string[]) : [];
        const isMs = p.id === MICROSOFT;
        // The Workspace-domain check is Google's only: Microsoft serves workspace_domain null.
        const domain = isMs ? "" : (p.cfg?.workspace_domain || "");
        const domainMismatch = !!domain && !!email.trim() && email.trim().toLowerCase().split("@").pop() !== domain;
        const err = rowErr && rowErr.provider === p.id ? rowErr.text : "";
        const open = openFor === p.id;
        const status = conn ? (conn.status === "error" ? "error" : conn.status === "paused" ? "paused" : "connected") : ready ? "none" : "setup";
        const statusLine = status === "connected" ? (conn.account_email || "—")
          : status === "error" ? (conn.account_email ? `${conn.account_email} · needs attention` : "Needs attention")
          : status === "paused" ? (conn.account_email ? `${conn.account_email} · paused` : "Paused")
          : status === "none" ? "Not connected"
          : "Not available yet";
        /* "Last synced" means a pass COMPLETED successfully (CC 0208): last_sync_at is also
     set at connect/resume, and last_sync_completed_at is when the last pass ended,
     failed or not. An error pass shows the card's error state instead; no pass yet
     shows nothing. */
  const lastSync = conn && conn.last_sync_result === "success" ? conn.last_sync_completed_at : null;
        return (
          <div key={p.id} data-testid="calendar-provider-row" style={{ border: `1px solid ${status === "error" ? "#F3D0CB" : T.border}`, borderRadius: 12, background: status === "setup" ? T.n6 : T.white, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 18px", flexWrap: "wrap" }}>
              <span style={{ opacity: status === "setup" ? 0.55 : 1, display: "inline-flex" }}>
                <ProviderLogo id={isMs ? "outlook" : "google-cal"} name={p.label} brand={isMs ? "#0078D4" : "#4285F4"} glyph={isMs ? "M" : "G"} size={40} radius={10} />
              </span>
              <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{p.label}</span>
                  {status === "connected" ? <Pill tone="success" dot>{t("settings.calendarSync.stateConnected", "Connected")}</Pill>
                    : status === "error" ? <Pill tone="danger">{t("settings.calendarSync.stateError", "Error")}</Pill>
                    : status === "paused" ? <Pill tone="warn">{t("settings.calendarSync.statePaused", "Paused")}</Pill> : null}
                </div>
                <div style={{ fontSize: 12.5, color: status === "connected" ? T.n1 : T.soft, marginTop: 3, overflowWrap: "anywhere" }}>{statusLine}</div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginLeft: "auto" }}>
                {/* Disconnect only ASKS: it goes through a confirm, and Cancel issues no request. */}
                {conn && canWrite ? <Btn variant="secondary" size="sm" disabled={busy} onClick={() => setConfirmingDisconnect(p.id)}>{t("settings.calendarSync.disconnect", "Disconnect")}</Btn> : null}
                {!conn && ready && !open && canWrite ? <Btn variant="primary" size="sm" disabled={busy} onClick={() => { setOpenFor(p.id); setRowErr(null); }}>{t("settings.calendarSync.connectShort", "Connect")}</Btn> : null}
              </div>
            </div>

            {conn ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 1, background: T.border, borderTop: `1px solid ${T.border}` }}>
                {[
                  { k: "Calendar", v: calLabel(conn.calendar_id) },
                  { k: "Sync", v: dirLabel(String(conn.sync_direction || "")) },
                  ...(lastSync ? [{ k: "Last synced", v: friendlyDateTime(String(lastSync)) }] : []),
                ].map((f) => (
                  <div key={f.k} style={{ background: T.white, padding: "10px 18px" }}>
                    <div style={{ fontSize: 11, color: T.soft, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600 }}>{f.k}</div>
                    <div style={{ fontSize: 13, color: T.n1, marginTop: 2 }}>{f.v}</div>
                  </div>
                ))}
              </div>
            ) : null}

            {conn && conn.error_message ? (
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "10px 18px", borderTop: "1px solid #F3D0CB", background: "#FDECEA", fontSize: 12, color: "#8C1D18", lineHeight: 1.5 }}>
                <span style={{ display: "inline-flex", marginTop: 2 }}><Icon.warn size={13} /></span><span>{conn.error_message}</span>
              </div>
            ) : null}
            {/* The delay is real, so it is stated rather than hidden behind a spinner. */}
            {conn && justConnected === p.id ? (
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "10px 18px", borderTop: "1px solid #EBD9AE", background: "#FAF1DE", fontSize: 12, color: "#6B4E00", lineHeight: 1.5 }}>
                <span style={{ display: "inline-flex", marginTop: 2 }}><Icon.clock size={13} /></span>
                <span>Connected. Changes made in {isMs ? "Microsoft 365" : "Google"} can take about five minutes to start flowing in.</span>
              </div>
            ) : null}

            {status === "setup" ? (
              <div style={{ padding: "0 18px 14px 72px", ...help }}>
                {issues.length ? `Not set up: ${issues.join("; ")}.` : setupHint(p.id)}{" "}
                <a href="#" onClick={(ev) => { ev.preventDefault(); nav.go("settings.integ.detail", { id: isMs ? "microsoft" : "google" }); }}>Set it up in Integrations</a>
              </div>
            ) : null}
            {status === "none" && !open && !canWrite ? (
              <div style={{ padding: "0 18px 14px 72px", ...help }}>Connecting a calendar needs the Integrations edit permission.</div>
            ) : null}

            {open ? (
              <div style={{ borderTop: `1px solid ${T.border}`, background: "#FAFAFC", padding: 18, display: "grid", gap: 16 }}>
                <label style={{ display: "grid", gap: 6, maxWidth: 420 }}>
                  <span style={fieldLabel}>{isMs ? "Mailbox address" : t("settings.calendarSync.workspaceEmail", "Workspace email")}</span>
                  <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={domain ? t("settings.calendarSyncPanels.placeholder4", "name@{domain}", { domain }) : t("settings.calendarSyncPanels.placeholder5", "name@example.edu")} disabled={busy} />
                  {domainMismatch ? <span style={{ fontSize: 11.5, color: "#8C1D18" }}>{t("settings.calendarSync.mustBeInDomain", "Must be an address in")} {domain}.</span>
                    : <span style={help}>{isMs ? "The Microsoft 365 mailbox whose calendar this person uses." : domain ? `An address in ${domain}.` : "The Google Workspace address whose calendar this person uses."}</span>}
                </label>
                <label style={{ display: "grid", gap: 6, maxWidth: 420 }}>
                  <span style={fieldLabel}>{t("settings.calendarSync.calendarId", "Calendar")}</span>
                  <Input value={calendarId} onChange={(e) => setCalendarId(e.target.value)} placeholder={t("settings.calendarSyncPanels.placeholder6", "primary")} disabled={busy} />
                  <span style={help}>Leave as "primary" for their main calendar.</span>
                </label>
                <div role="radiogroup" aria-label={t("settings.calendarSync.direction", "Sync direction")} style={{ display: "grid", gap: 8 }}>
                  <span style={fieldLabel}>{t("settings.calendarSync.direction", "Sync direction")}</span>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 8 }}>
                    {DIRECTIONS.map((d) => {
                      const on = direction === d.id;
                      return (
                        <button key={d.id} type="button" role="radio" aria-checked={on} disabled={busy} onClick={() => setDirection(d.id)}
                          style={{ textAlign: "left", padding: "10px 12px", borderRadius: 10, cursor: busy ? "default" : "pointer", fontFamily: "inherit", border: `${on ? 2 : 1}px solid ${on ? T.p1 : T.border}`, background: on ? T.p3 : T.white, display: "grid", gap: 3 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: on ? T.p1 : T.n1 }}>{d.label}</span>
                          <span style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.45 }}>{d.sub}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {err ? <div role="alert" style={{ fontSize: 12, color: "#8C1D18", lineHeight: 1.5 }}>{err}</div> : null}
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
                  <Btn variant="ghost" size="sm" disabled={busy} onClick={() => { setOpenFor(""); setRowErr(null); }}>{t("common.actions.cancel", "Cancel")}</Btn>
                  <Btn variant="primary" size="sm" disabled={busy || !email.trim() || domainMismatch} onClick={() => { void connect(p.id, domain); }}>{busy ? t("settings.calendarSync.connecting", "Connecting…") : `Connect ${p.label}`}</Btn>
                </div>
              </div>
            ) : err ? <div role="alert" style={{ padding: "0 18px 14px 72px", fontSize: 12, color: "#8C1D18", lineHeight: 1.5 }}>{err}</div> : null}

            {conn ? (
              <ConfirmDialog
                open={confirmingDisconnect === p.id}
                tone="danger"
                busy={busy}
                title={`Disconnect this ${p.label}?`}
                body={conn.account_email ? <b>{conn.account_email}</b> : undefined}
                consequence={t("settings.calendarSync.disconnectConsequence", "New and changed bookings stop reaching this calendar, and events in it stop blocking this person's availability. You can connect it again later.")}
                confirmLabel={busy ? t("settings.calendarSync.disconnecting", "Disconnecting…") : t("settings.calendarSync.disconnect", "Disconnect")}
                cancelLabel={t("common.actions.cancel", "Cancel")}
                onCancel={() => { if (!busy) setConfirmingDisconnect(""); }}
                onConfirm={() => { void disconnect(p.id); }}
                testid="calendar-disconnect-confirm"
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/* ═══ ASK 4 — the tenant's sync status ═════════════════════════════════════ */

/** Tenant-wide calendar-sync state: is it configured, who is connected, what is failing. */
export function CalendarSyncStatusPanel() {
  const t = useT();
  const [rows, setRows] = useState<any[] | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [attention, setAttention] = useState<any[]>([]);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const [status, health] = await Promise.all([getCalendarSyncStatus(), getCalendarSyncHealth()]);
      setRows(status.data as any[]);
      setSummary((health as any).data.summary);
      setAttention((health as any).data.attention || []);
    } catch (e) {
      setRows([]);
      setErr(errText(e, t("settings.calendarSync.statusFailed", "Couldn't read calendar sync status.")));
    }
  }, [t]);
  useEffect(() => { void load(); }, [load]);

  if (rows === null) return <Card style={{ padding: 16, marginTop: 14 }}><span style={{ fontSize: 12, color: T.soft }}>{t("common.loading", "Loading…")}</span></Card>;

  return (
    <Card style={{ padding: 16, marginTop: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Icon.refresh size={14} />
          <b style={{ fontSize: 13.5, color: T.n1 }}>{t("settings.calendarSync.statusTitle", "Calendar sync")}</b>
        </div>
        <Btn variant="ghost" size="sm" onClick={() => { void load(); }}>{t("common.actions.refresh", "Refresh")}</Btn>
      </div>

      {err ? <div style={{ fontSize: 12, color: "#8C1D18", background: "#FCEEEE", border: "1px solid #F3C9C6", borderRadius: 8, padding: "8px 10px", marginBottom: 10 }}>{err}</div> : null}

      {summary ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, fontSize: 12.5, marginBottom: 12 }}>
          <span><b style={{ fontSize: 15 }}>{summary.total_connections}</b> <span style={{ color: T.soft }}>{t("settings.calendarSync.connected", "connected")}</span></span>
          <span><b style={{ fontSize: 15, color: summary.error ? "#8C1D18" : T.n1 }}>{summary.error}</b> <span style={{ color: T.soft }}>{t("settings.calendarSync.failing", "failing")}</span></span>
          <span><b style={{ fontSize: 15, color: summary.stale_connections ? "#916D0F" : T.n1 }}>{summary.stale_connections}</b> <span style={{ color: T.soft }}>{t("settings.calendarSync.stale", "not synced in 24h")}</span></span>
          <span><b style={{ fontSize: 15 }}>{summary.expiring_channels}</b> <span style={{ color: T.soft }}>{t("settings.calendarSync.expiring", "channels expiring")}</span></span>
        </div>
      ) : null}

      {!rows.length ? (
        <div style={{ fontSize: 12, color: T.soft, lineHeight: 1.55 }}>
          {t("settings.calendarSync.noneConnected", "Nobody's calendar is connected yet. Connect one from a staff member's record.")}
        </div>
      ) : (
        <div style={{ display: "grid", gap: 6 }}>
          {rows.map((r) => {
            const bad = r.status === "error";
            return (
              <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "8px 10px", border: `1px solid ${bad ? "#F3C9C6" : T.border}`, background: bad ? "#FDF6F6" : "#FFFFFF", borderRadius: 8 }}>
                <Pill tone={(bad ? "danger" : r.status === "paused" ? "warn" : "success") as any}>{r.status}</Pill>
                <span style={{ fontSize: 12.5, color: T.n1, minWidth: 180 }}>{r.account_email || r.staff_id}</span>
                <span style={{ fontSize: 11.5, color: T.soft }}>{r.last_sync_at ? t("settings.calendarSyncPanels.text2", "{arg1} {arg2}", { arg1: t("settings.calendarSync.lastSync", "last sync"), arg2: new Date(r.last_sync_at).toLocaleString() }) : t("settings.calendarSync.neverSynced", "no sync yet")}</span>
                {r.watch_status ? <span style={{ fontSize: 11, color: T.soft }}>· {String(r.watch_status).replace(/_/g, " ")}</span> : null}
                {r.error_message ? <span style={{ fontSize: 11.5, color: "#8C1D18", flexBasis: "100%" }}>{r.error_message}</span> : null}
              </div>
            );
          })}
        </div>
      )}

      {attention.length ? (
        <div style={{ fontSize: 11.5, color: "#916D0F", marginTop: 10 }}>
          {attention.length} {t("settings.calendarSync.needAttention", "need attention.")}
        </div>
      ) : null}
    </Card>
  );
}
